import {useCompetitionImamrezaContext} from '@context/CompetitionImamrezaContext';
import {useScopedTranslation} from '@hooks/useScopedTranslation';
import CloseIcon from '@mui/icons-material/Close';
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton} from '@mui/material';
import {motion} from 'framer-motion';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import bg0 from './assets/images/m2/bg-0.webp';
import bg1 from './assets/images/m2/bg-1.webp';
import bg2 from './assets/images/m2/bg-2.webp';
import bg3 from './assets/images/m2/bg-3.webp';
import bg4 from './assets/images/m2/bg-4.webp';
import bg5 from './assets/images/m2/bg-5.webp';
import bg6 from './assets/images/m2/bg-6.webp';
import bg7 from './assets/images/m2/bg-7.webp';

interface Step {
	name?: string;
	text1: string;
	text2: string;
	question: string;
	help: string;
	x1: string | number;
	y1: string | number;
	x2: string | number;
	y2: string | number;
	bgColor: string;
	bgImage: string;
	textStyle: string;
	triangleStyle: string;
	questionStyle: string;
}

const steps: Step[] = [
	{
		name: 'bg0',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '10%',
		y1: '40%',
		x2: '40%',
		y2: '75%',
		bgColor: '#fff',
		bgImage: bg0,
		textStyle: 'bg-[#188A6F] text-white',
		triangleStyle: 'border-[#188A6F] bg-transparent',
		questionStyle: 'text-[#188A6F] bg-white'
	},
	{
		name: 'bg2',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '25%',
		y1: '60%',
		x2: '30%',
		y2: '80%',
		bgColor: '#fff',
		bgImage: bg2,
		textStyle: 'bg-cyan-700 text-white',
		triangleStyle: 'border-cyan-700 bg-transparent',
		questionStyle: 'text-cyan-700 bg-white'
	},
	{
		name: 'bg7',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '40%',
		y1: '25%',
		x2: '30%',
		y2: '30%',
		bgColor: '#fff',
		bgImage: bg7,
		textStyle: 'bg-[#E64900] text-white',
		triangleStyle: 'border-[#E64900] bg-transparent',
		questionStyle: 'text-[#E64900] bg-white'
	},
	{
		name: 'bg6',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '55%',
		y1: '20%',
		x2: '3%',
		y2: '70%',
		bgColor: '#fff',
		bgImage: bg6,
		textStyle: 'bg-cyan-700 text-white',
		triangleStyle: 'border-cyan-700 bg-transparent',
		questionStyle: 'text-cyan-700 bg-white'
	},
	{
		name: 'bg1',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '30%',
		y1: '20%',
		x2: '20%',
		y2: '40%',
		bgColor: '#fff',
		bgImage: bg1,
		textStyle: 'bg-orange-700 text-white',
		triangleStyle: 'border-orange-700 bg-transparent',
		questionStyle: 'text-orange-700 bg-white'
	},
	{
		name: 'bg3',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '30%',
		y1: '12%',
		x2: '30%',
		y2: '30%',
		bgColor: '#fff',
		bgImage: bg3,
		textStyle: 'bg-red-900 text-white',
		triangleStyle: 'border-red-900 bg-transparent',
		questionStyle: 'text-red-900 bg-white'
	},
	{
		name: 'bg4',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '75%',
		y1: '35%',
		x2: '20%',
		y2: '45%',
		bgColor: '#fff',
		bgImage: bg4,
		textStyle: 'bg-[#EB7A01] text-white',
		triangleStyle: 'border-[#EB7A01] bg-transparent',
		questionStyle: 'text-[#EB7A01] bg-white'
	},
	{
		name: 'bg5',
		text1: 'text1',
		text2: 'text2',
		question: 'question',
		help: 'help',
		x1: '40%',
		y1: '12%',
		x2: '40%',
		y2: '45%',
		bgColor: '#fff',
		bgImage: bg5,
		textStyle: 'bg-indigo-900 text-white',
		triangleStyle: 'border-indigo-900 bg-transparent',
		questionStyle: 'text-indigo-900 bg-white'
	}
];

export default function AnimatedChoices() {
	const {t} = useScopedTranslation();
	const navigate = useNavigate();
	const [step, setStep] = useState(0);
	const [isImageLoaded, setIsImageLoaded] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [fadeText, setFadeText] = useState(false);
	const [fadeText3, setFadeText3] = useState(false);
	const [bgFade, setBgFade] = useState(false);
	const [loadedBg, setLoadedBg] = useState(steps[0].bgImage);
	const {
		setMission,
		setResults,
		// setSelectedIndices,
		// selectedIndices,
		lang,
		results
	} = useCompetitionImamrezaContext();
	
	const preloadImage = (src: string) =>
		new Promise<void>((resolve) => {
			const img = new Image();
			img.src = src;
			img.onload = () => resolve();
		});
	
	useEffect(() => {
		if (step < steps.length - 1) {
			setIsImageLoaded(false);
			preloadImage(steps[step + 1].bgImage).then(() => setIsImageLoaded(true));
		}
		if (step === steps.length - 1) {
			setTimeout(() => setMission(2), 0);
		}
	}, [step]);
	
	const handleClick = async (index: number) => {
		const updated = [...results];
		// Ensure the array has enough elements
		// while (updated.length <= step) {
		// 	updated.push(Number('')); // or -1, or any default
		// }
		updated[step] = index;
		setResults(updated);
		if (step < steps.length - 1) {
			setFadeText(true);
			setFadeText3(true);
			setBgFade(true);
			await preloadImage(steps[step + 1].bgImage);
			setTimeout(() => {
				setLoadedBg(steps[step + 1].bgImage);
				setStep((prev) => prev + 1);
				setFadeText(false);
				setFadeText3(false);
				setBgFade(false);
			}, 500);
		} else {
			if (!isImageLoaded) await preloadImage(steps[step].bgImage);
			setMission(3);
		}
	};
	
	return (
		<div
			className="w-full h-full flex items-center justify-center relative transition-all duration-500 ease-in-out bg-no-repeat"
			style={{
				backgroundImage: `url(${loadedBg})`,
				backgroundSize: 'cover',
				backgroundPosition: 'center center'
			}}
		>
			<motion.div
				className="absolute top-0 left-0 w-full h-full transition-opacity duration-500"
				animate={{opacity: bgFade ? 0 : 1}}
				style={{backgroundColor: steps[step].bgColor, zIndex: -1}}
			/>
			
			{/* Question */}
			<motion.div
				className="absolute bottom-[10%] text-center w-full items-center justify-center flex flex-row"
				initial={{opacity: 1}}
				animate={{opacity: fadeText3 ? 0 : 1}}
				transition={{duration: 0.5}}
				// style={steps[step].text3Style}
			>
				<div
					className={`w-fit h-auto px-3 pt-1 text-center rounded-2xl font-bold text-3xl ${steps[step].questionStyle}`}
					style={{direction: `${lang == 'en' ? 'ltr' : 'rtl'}`}}
				>
					{t(`steps.step-${step}.question`)}
				</div>
			</motion.div>
			
			{/* Option 1 */}
			<motion.div
				className={`absolute flex items-center justify-center cursor-pointer`}
				style={{top: steps[step].y1, left: steps[step].x1}}
				onClick={() => handleClick(0)}
				animate={{top: steps[step].y1, left: steps[step].x1}}
				transition={{duration: 0.8, ease: 'easeInOut'}}
			>
				<motion.span
					// style={steps[step].text1Style}
					initial={{opacity: 1}}
					animate={{opacity: fadeText ? 0.5 : 1}}
					transition={{duration: 0.3}}
				>
					<div className="relative w-full mx-1">
						<div
							className={`w-full h-auto px-2 py-1 text-center rounded-2xl font-bold text-lg ${steps[step].textStyle}`}>
							{t(`steps.step-${step}.text1`)}
						</div>
						<div
							className={`absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent ${steps[step].triangleStyle}`}
						/>
					</div>
				</motion.span>
			</motion.div>
			
			{/* Option 2 */}
			<motion.div
				className={`absolute flex items-center justify-center cursor-pointer`}
				// className={`absolute flex items-center justify-center cursor-pointer
				//   ${selectedIndices[step] === 1 ? 'bg-green-400' : 'bg-gray-300'}
				//  `}
				style={{bottom: steps[step].y2, left: steps[step].x2}}
				onClick={() => handleClick(1)}
				animate={{bottom: steps[step].y2, left: steps[step].x2}}
				transition={{duration: 0.8, ease: 'easeInOut'}}
			>
				<motion.span
					// style={steps[step].text2Style}
					initial={{opacity: 1}}
					animate={{opacity: fadeText ? 0.5 : 1}}
					transition={{duration: 0.3}}
				>
					<div className="relative w-full mx-1">
						<div
							className={`w-full h-auto px-2 py-1 text-center rounded-2xl font-bold text-lg ${steps[step].textStyle}`}>
							{t(`steps.step-${step}.text2`)}
						</div>
						<div
							className={`absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent ${steps[step].triangleStyle}`}
						/>
					</div>
				</motion.span>
			</motion.div>
			
			{/* Optional Dialog */}
			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
				<DialogTitle>توضیحات</DialogTitle>
				<IconButton
					aria-label="close"
					onClick={() => setDialogOpen(false)}
					sx={{position: 'absolute', right: 8, top: 8}}
				>
					<CloseIcon/>
				</IconButton>
				<DialogContent>
					<DialogContentText>{steps[step].help}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>فهمیدم</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
